/** PELU-08: paquetes de sesiones (capability `service_packages`). */

export type ServicePackageItem = {
  productId: string;
  sortOrder: number;
  name: string | null;
};

export type ServicePackage = {
  id: string;
  organizationId: string;
  branchId: string | null;
  name: string;
  price: number;
  sessionCount: number;
  validityDays: number;
  active: boolean;
  sellOnPortal: boolean;
  items: ServicePackageItem[];
  createdAt?: string;
  updatedAt?: string;
};

export type CustomerPackageEntitlementStatus =
  | 'active'
  | 'exhausted'
  | 'expired'
  | 'void'
  | 'pending_payment'
  | string;

export type CustomerPackageEntitlement = {
  id: string;
  organizationId: string;
  packageId: string;
  customerId: string | null;
  customerUserId: string | null;
  sessionsTotal: number;
  sessionsRemaining: number;
  expiresAt: string;
  saleId: string | null;
  status: CustomerPackageEntitlementStatus;
  packageName: string;
  packagePrice: number;
  productIds: string[];
  createdAt?: string;
};

export type PurchaseServicePackageResult = {
  ok: boolean;
  entitlementId: string;
  sessionsRemaining: number;
  expiresAt: string;
  price: number;
  saleId?: string | null;
  status?: CustomerPackageEntitlementStatus;
  requiresPayment?: boolean;
  idempotent?: boolean;
};

export type PackagePaymentMethodId = 'cash' | 'transfer' | 'mercadopago_qr';

type RawPackageItem = {
  product_id?: unknown;
  sort_order?: unknown;
  name?: unknown;
};

type RawServicePackage = {
  id?: unknown;
  organization_id?: unknown;
  branch_id?: unknown;
  name?: unknown;
  price?: unknown;
  session_count?: unknown;
  validity_days?: unknown;
  active?: unknown;
  sell_on_portal?: unknown;
  items?: unknown;
  created_at?: unknown;
  updated_at?: unknown;
};

type RawEntitlement = {
  id?: unknown;
  organization_id?: unknown;
  package_id?: unknown;
  customer_id?: unknown;
  customer_user_id?: unknown;
  sessions_total?: unknown;
  sessions_remaining?: unknown;
  expires_at?: unknown;
  sale_id?: unknown;
  status?: unknown;
  package_name?: unknown;
  package_price?: unknown;
  product_ids?: unknown;
  created_at?: unknown;
};

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function asBool(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function mapPackageItem(raw: RawPackageItem): ServicePackageItem | null {
  const productId = asString(raw.product_id).trim();
  if (!productId) return null;
  return {
    productId,
    sortOrder: asNumber(raw.sort_order, 0),
    name: typeof raw.name === 'string' ? raw.name : null,
  };
}

export function mapServicePackage(raw: unknown): ServicePackage | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as RawServicePackage;
  const id = asString(o.id).trim();
  if (!id) return null;
  const itemsRaw = Array.isArray(o.items) ? o.items : [];
  return {
    id,
    organizationId: asString(o.organization_id),
    branchId: typeof o.branch_id === 'string' && o.branch_id.trim() ? o.branch_id : null,
    name: asString(o.name),
    price: asNumber(o.price),
    sessionCount: asNumber(o.session_count, 1),
    validityDays: asNumber(o.validity_days, 180),
    active: asBool(o.active, true),
    sellOnPortal: asBool(o.sell_on_portal, false),
    items: itemsRaw
      .map((item) => mapPackageItem(item as RawPackageItem))
      .filter((x): x is ServicePackageItem => x !== null),
    createdAt: typeof o.created_at === 'string' ? o.created_at : undefined,
    updatedAt: typeof o.updated_at === 'string' ? o.updated_at : undefined,
  };
}

export function mapCustomerPackageEntitlement(raw: unknown): CustomerPackageEntitlement | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as RawEntitlement;
  const id = asString(o.id).trim();
  if (!id) return null;
  const productIdsRaw = Array.isArray(o.product_ids) ? o.product_ids : [];
  return {
    id,
    organizationId: asString(o.organization_id),
    packageId: asString(o.package_id),
    customerId: typeof o.customer_id === 'string' && o.customer_id.trim() ? o.customer_id : null,
    customerUserId:
      typeof o.customer_user_id === 'string' && o.customer_user_id.trim()
        ? o.customer_user_id
        : null,
    sessionsTotal: asNumber(o.sessions_total),
    sessionsRemaining: asNumber(o.sessions_remaining),
    expiresAt: asString(o.expires_at),
    saleId: typeof o.sale_id === 'string' && o.sale_id.trim() ? o.sale_id : null,
    status: asString(o.status, 'active'),
    packageName: asString(o.package_name),
    packagePrice: asNumber(o.package_price),
    productIds: productIdsRaw
      .map((id) => (typeof id === 'string' ? id.trim() : ''))
      .filter(Boolean),
    createdAt: typeof o.created_at === 'string' ? o.created_at : undefined,
  };
}

/** True if every selected product is covered by the entitlement's package items. */
export function entitlementCoversProducts(
  entitlement: Pick<CustomerPackageEntitlement, 'productIds'>,
  productIds: string[],
): boolean {
  if (productIds.length === 0) return false;
  const covered = new Set(entitlement.productIds);
  return productIds.every((id) => covered.has(id));
}
